import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess } from '../types';

// ---- Invoices ----
export interface Invoice {
  id: number; code: string; omsId: number; partyType: string; vendorId: number | null; clientId: number | null;
  invoiceNumber: string | null; invoiceDate: string | null; dueDate: string | null;
  amount: number; taxAmount: number; totalAmount: number; paidAmount: number; status: string; fileUrl: string | null; createdAt: string;
}
export interface CreateInvoiceInput {
  omsId: number; partyType: 'vendor' | 'client'; vendorId?: number | null; clientId?: number | null;
  referenceType?: string | null; referenceId?: number | null; invoiceNumber?: string; invoiceDate?: string; dueDate?: string; amount: number; taxAmount?: number; fileUrl?: string;
}
export const invoicesApi = createCrudApi<Invoice, CreateInvoiceInput>('invoices');

// ---- Payments ----
export interface PaymentDoc { id: number; docType: string; fileName: string; fileUrl: string; version: number; isCurrent: boolean }
export interface Payment {
  id: number; code: string; omsId: number; partyType: string; vendorId: number | null; clientId: number | null; invoiceId: number | null;
  paymentType: string; amount: number; taxAmount: number; totalAmount: number; status: string; dueDate: string | null;
  utrNumber: string | null; paidDate: string | null; remarks: string | null; documents?: PaymentDoc[]; createdAt: string;
}
export interface CreatePaymentInput {
  omsId: number; partyType: 'vendor' | 'client'; vendorId?: number | null; clientId?: number | null; invoiceId?: number | null;
  paymentType: string; amount: number; taxAmount?: number; dueDate?: string; remarks?: string;
}
const payBase = createCrudApi<Payment, CreatePaymentInput>('payments');
export const paymentsApi = {
  ...payBase,
  async changeStatus(id: number, body: { action: string; remarks?: string; utrNumber?: string; paidDate?: string }) {
    const { data } = await apiClient.post<ApiSuccess<Payment>>(`/payments/${id}/status`, body);
    return data.data;
  },
  async addDocument(id: number, input: { docType: string; fileName: string; fileUrl: string }) {
    const { data } = await apiClient.post<ApiSuccess<PaymentDoc>>(`/payments/${id}/documents`, input);
    return data.data;
  },
};

// ---- Finance analytics ----
export interface FinanceDashboard {
  totalPayable: number; totalReceivable: number; totalPaidOut: number; totalReceived: number;
  pendingApprovals: number; pendingVerification: number; awaitingRelease: number; overduePayments: number;
  profitability: { clientInvoiced: number; vendorInvoiced: number; grossMargin: number };
}
export interface LedgerRow { id: number; entryType: string; referenceCode: string | null; debit: number; credit: number; narration: string | null; entryDate: string | null; balance: number }
export const financeApi = {
  async dashboard(): Promise<FinanceDashboard> { const { data } = await apiClient.get<ApiSuccess<FinanceDashboard>>('/finance/dashboard'); return data.data; },
  async reminders(): Promise<{ awaitingAction: { id: number; code: string; status: string; totalAmount: number; dueDate: string | null }[]; overdue: { id: number; code: string; status: string; totalAmount: number; dueDate: string | null }[] }> {
    const { data } = await apiClient.get<ApiSuccess<{ awaitingAction: never[]; overdue: never[] }>>('/finance/reminders'); return data.data;
  },
  async ageing(partyType: string): Promise<{ partyType: string; buckets: Record<string, number> }> { const { data } = await apiClient.get<ApiSuccess<{ partyType: string; buckets: Record<string, number> }>>('/finance/ageing', { params: { partyType } }); return data.data; },
  async ledger(partyType: string, partyId: number): Promise<{ outstanding: number; entries: LedgerRow[] }> { const { data } = await apiClient.get<ApiSuccess<{ outstanding: number; entries: LedgerRow[] }>>('/finance/ledger', { params: { partyType, partyId } }); return data.data; },
};
